// 블록 ① 에디토리얼 히어로 + Branded Engagement 배지.
//
// 히어로는 언제나 다크 편집 배경(ink) → 흰 텍스트가 라이트/다크 공통 안전.
// 크리에이터 themeColor(데이터값)에서 hexA로 틴트/글래스 파생.
// photoUrl 없으면 다크 배경만(graceful). brandedEngagement 비유한값이면 배지 숨김(현행 유지 —
//   값이 없을 때 "—" 배지를 띄우면 다크 히어로에서 의미 없는 잔해가 된다).
// 표본이 적으면(n<5) **값은 그대로 두고** 모수를 배지 안에 명시한다. 숨기지 않는 게 원칙이다.
import type { CreatorSummary, DataMeta, Platform } from '../../creatorsApi'
import { LOW_SAMPLE_MIN } from '../metric'
import { PLATFORM_LABEL, buildBasisNote, formatRate, hexA } from './format'
import { formatHandle } from '../profile/format'

interface Props {
  creator: CreatorSummary
  /** 채널별 브랜디드 인게이지먼트 — **세 채널이 한 번에** 뜬다. 값이 유한한 채널만 그린다.
   *  탭을 따라가지 않는다: 탭을 만진 적 없는 사람이 한 채널 값을 전체 값으로 읽으면 안 된다. */
  engagement: { platform: Platform; value: number; sample: number | null }[]
  /** 집계창·수집 기준일 — 배지 수치의 근거 3요소를 채운다. */
  meta?: DataMeta
}

// 텍스트 가독성 스크림 — ink 토큰에서 파생(인라인 hex 아님).
const TEXT_SCRIM =
  'linear-gradient(90deg, color-mix(in srgb, var(--ink-800) 72%, transparent) 0%, ' +
  'color-mix(in srgb, var(--ink-800) 34%, transparent) 48%, transparent 76%)'

export function ProfileHero({ creator, engagement, meta }: Props) {
  const theme = creator.themeColor
  // themeColor 유효하면 파생 틴트, 아니면 중립 폴백(brand 토큰).
  const accent = hexA(theme, 1) ? theme : 'var(--brand-pink)'
  const heroTint =
    hexA(theme, 0.55) &&
    `radial-gradient(120% 130% at 100% 0%, ${hexA(theme, 0.55)} 0%, ${hexA(theme, 0.12)} 42%, rgba(0,0,0,0) 68%)`
  const glassBorder = hexA(theme, 0.55) ?? 'var(--border-on-dark)'
  const glassBg = hexA(theme, 0.16) ?? 'color-mix(in srgb, var(--paper) 12%, transparent)'
  // 값이 없는 채널은 배지에서 뺀다 — 다크 히어로에서 "—" 는 의미 없는 잔해가 된다(현행 원칙).
  const rows = engagement
    .filter((e) => Number.isFinite(e.value))
    .map((e) => ({
      ...e,
      lowSample: e.sample != null && e.sample > 0 && e.sample < LOW_SAMPLE_MIN,
    }))
  // 툴팁은 히어로의 overflow-hidden에 잘려 못 쓴다 → 같은 내용을 title + sr-only로 전달한다.
  //   근거 3요소(소스·집계창·수집일)는 다른 블록과 같은 빌더에서 뽑아 문구가 갈라지지 않게 한다.
  // hover 문구는 **산식·집계창·모수**만 말한다 — AI 고지는 별도 고지 줄이 담당한다.
  const engagementDetail = [
    buildBasisNote('협찬 영상의 (좋아요+댓글)÷조회수 평균', meta),
    rows.map((r) => `${PLATFORM_LABEL[r.platform]} ${r.sample ?? 0}건`).join(' · '),
  ]
    .filter(Boolean)
    .join(' · ')
  // 이름 길이 기반 폰트 단계 축소 — 긴 이름이 3줄로 위계를 압도하지 않게(잘림 대신 축소).
  // 길이별 축소(유지) — 단계값을 DS 타입 스텝으로: display-xl 60 / h2 40 / h2 28.
  const nameLen = creator.name.trim().length
  const nameSizeClass = nameLen > 20 ? 'text-[28px]' : nameLen > 12 ? 'text-[40px]' : 'text-[60px]'
  // 서브라인 핸들 @ 규칙(Profile formatHandle 재사용) — 대표 채널(구독자 최대) 플랫폼 기준.
  const repChannel = creator.channels.reduce(
    (best, c) => (best && best.subscribers >= c.subscribers ? best : c),
    null as CreatorSummary['channels'][number] | null,
  )
  const heroHandle = formatHandle(repChannel?.platform ?? 'youtube', creator.handle)

  return (
    <div className="relative min-h-[300px] overflow-hidden bg-surface-ink">
      {creator.photoUrl && (
        <img
          src={creator.photoUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          }}
        />
      )}
      {heroTint && <div className="pointer-events-none absolute inset-0" style={{ background: heroTint }} />}
      <div className="pointer-events-none absolute inset-0" style={{ background: TEXT_SCRIM }} />

      {/* 글래스 프로필 카드 */}
      <div className="relative flex min-h-[300px] items-center p-[28px_32px]">
        <div
          className="min-w-0 max-w-[540px] border p-[26px_30px] backdrop-blur-[22px] backdrop-saturate-150"
          style={{ borderColor: glassBorder, borderLeft: `3px solid ${accent}`, background: glassBg }}
        >
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5" style={{ background: accent }} aria-hidden />
            <span className="font-text text-[11px] font-medium tracking-[0.2em] text-white/80">
              CREATOR PROFILE{creator.label ? ` · ${creator.label}` : ''}
            </span>
          </div>
          <div
            className={`mt-4 break-keep font-display ${nameSizeClass} font-bold leading-[0.94] tracking-[-0.01em] text-white`}
          >
            {creator.name}
            <span style={{ color: accent }}>.</span>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <span className="whitespace-nowrap font-text text-sm text-white/85">{heroHandle}</span>
            {creator.tagline && (
              <>
                <span className="inline-block h-[3px] w-[3px] bg-white/45" aria-hidden />
                <span className="whitespace-nowrap font-text text-sm text-white/85">{creator.tagline}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Branded Engagement 배지 — **세 채널을 한 번에** 보여준다.
          아래 캐러셀 탭을 따라가게 하면, 탭을 만진 적 없는 사람이 이 숫자를 크리에이터 전체
          값으로 읽는다. 채널마다 값이 다르므로 나란히 놓고 각자 라벨을 단다. */}
      {(
        <div
          className="absolute bottom-4 right-4 flex items-end gap-5 border p-[12px_15px] backdrop-blur-[18px] backdrop-saturate-150"
          style={{ borderColor: glassBorder, background: glassBg }}
          title={engagementDetail}
        >
          <div>
            <div className="font-text text-[11px] font-medium tracking-[0.16em] text-white/80">
              BRANDED ENGAGEMENT
            </div>
            {rows.length === 0 && (
              <div className="mt-[7px] font-text text-[12px] text-white/70">
                브랜디드로 판정된 콘텐츠가 없습니다
              </div>
            )}
            <div className="mt-[7px] flex items-end gap-5">
              {rows.map((r) => (
                <div key={r.platform} className="min-w-0">
                  <div className="font-text text-[10px] tracking-[0.06em] text-white/65">
                    {PLATFORM_LABEL[r.platform]}
                  </div>
                  <div className="mt-[3px] font-display text-[22px] font-bold leading-none tabular-nums text-white">
                    {formatRate(r.value)}
                  </div>
                  {r.sample != null && (
                    <div className="mt-[4px] flex items-center gap-1 font-text text-[10px] text-white/70">
                      {r.lowSample && (
                        <span
                          className="h-1.5 w-1.5 flex-none rounded-r-pill bg-status-warning"
                          role="img"
                          aria-label="저신뢰"
                        />
                      )}
                      {r.sample}건
                    </div>
                  )}
                </div>
              ))}
            </div>
            <span className="sr-only">{engagementDetail}</span>
          </div>
          {/* 포맷 라벨을 붙이지 않는다 — 2026-08-12 에 숏폼 한정이 제거돼 **협찬 전량**이 모수다.
              (숏폼 한정은 근거 없는 초기 정의였고, 협찬이 롱폼 위주인 크리에이터는 표본 0이 됐다.) */}
        </div>
      )}
    </div>
  )
}
